/* ============================================================================
 * Movatic WMS — Frontend | CesarFilho 932048016
 * Arquivo: src/pages/workforce/Equipments.tsx
 * ----------------------------------------------------------------------------
 * Lista de equipamentos do Workforce com busca e refresh.
 * - Fallback de endpoints: /workforce/equipments/ ou /workforce/equipment/
 * - Aceita payload DRF paginado ({count, results}) ou lista direta ([]).
 * - Badges de Status: fundo colorido, TEXTO PRETO no claro e BRANCO no dark.
 * ========================================================================== */

import React, { useMemo, useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { apiGet } from "@/lib/api";
import Pagination from "@/components/Pagination";

// -------------------------- Tipos tolerantes --------------------------------
type IdOr<T> = number | string | T | null | undefined;

type Named = { id?: number; code?: string; name?: string };
type ParkingSpot = { id?: number; name?: string; location?: Named };
type Location = Named;
type Company = Named;
type Site = Named;
type Warehouse = Named;
type EquipmentType = Named;

type Equipment = {
    id: number;
    code: string;
    status?: "available" | "busy" | "maintenance" | "offline" | string;
    is_active?: boolean;
    description?: string | null;
    serial_no?: string | null;

    company?: IdOr<Company>;
    site?: IdOr<Site>;
    warehouse?: IdOr<Warehouse>;
    type?: IdOr<EquipmentType>;
    type_image?: string | null;
    current_location?: IdOr<Location>;
    parking_spot?: IdOr<ParkingSpot>;
};

type Paginated<T> = { count: number; next: string | null; previous: string | null; results: T[] };

// -------------------------- Helpers de exibição ------------------------------
function showNamed(v: IdOr<Named>, fallback = "—"): string {
    if (v == null) return fallback;
    if (typeof v === "number" || typeof v === "string") return String(v);
    return (v.code || v.name || fallback) as string;
}
function showParking(v: IdOr<ParkingSpot>, fallback = "—"): string {
    if (v == null) return fallback;
    if (typeof v === "number" || typeof v === "string") return String(v);
    return v.name || fallback;
}
function cls(...xs: Array<string | false | null | undefined>) {
    return xs.filter(Boolean).join(" ");
}

/** Classes das “caixas” de status:
 * - Claro: texto preto (text-neutral-900)
 * - Dark: texto branco (dark:text-neutral-100)
 * - Fundo colorido e leve borda do mesmo tom para contraste.
 */
function statusBadgeClasses(status?: string) {
    const base =
        "inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded border " +
        "text-neutral-900 dark:text-neutral-100";
    switch ((status || "").toLowerCase()) {
        case "available":
            return cls(
                base,
                "bg-green-300/80 border-green-400/80",
                "dark:bg-green-600/30 dark:border-green-500/50"
            );
        case "busy":
            return cls(
                base,
                "bg-blue-300/80 border-blue-400/80",
                "dark:bg-blue-600/30 dark:border-blue-500/50"
            );
        case "maintenance":
            return cls(
                base,
                "bg-yellow-300/80 border-yellow-400/80",
                "dark:bg-yellow-600/30 dark:border-yellow-500/50"
            );
        case "offline":
            return cls(
                base,
                "bg-neutral-300/80 border-neutral-400/80",
                "dark:bg-neutral-600/30 dark:border-neutral-500/50"
            );
        default:
            return cls(
                base,
                "bg-slate-200/80 border-slate-300/80",
                "dark:bg-slate-600/30 dark:border-slate-500/50"
            );
    }
}

const PAGE_SIZE = 20;

// -------------------------- Fetch com fallback -------------------------------
async function fetchEquipments(term: string, page: number) {
    const endpoints = ["/workforce/equipments/", "/workforce/equipment/"];

    for (const path of endpoints) {
        try {
            const res = await apiGet<any>(path, {
                params: {
                    page,
                    page_size: PAGE_SIZE,
                    search: term || undefined,
                    ordering: "company,site,warehouse,code",
                },
            });

            const data = res.data;
            const items: Equipment[] = Array.isArray(data)
                ? data
                : Array.isArray((data as Paginated<Equipment>)?.results)
                    ? (data as Paginated<Equipment>).results
                    : [];

            if (items.length || Array.isArray(data) || (data && "results" in data)) {
                const total = (data && (data as Paginated<Equipment>).count) ?? items.length;
                return { items, total, endpoint: path };
            }
        } catch {
            /* tenta o próximo endpoint */
        }
    }

    throw new Error("Não foi possível carregar equipamentos (endpoints não encontrados).");
}

// ------------------------------- Componente ----------------------------------
export default function WorkforceEquipments() {
    const [term, setTerm] = useState("");
    const [page, setPage] = useState(1);

    const q = useQuery({
        queryKey: ["wf-equipments", term, page],
        queryFn: () => fetchEquipments(term, page),
        staleTime: 30_000,
        placeholderData: keepPreviousData,
    });

    const rows = useMemo(() => q.data?.items ?? [], [q.data]);

    return (
        <div className="p-4">
            {/* Cabeçalho/linha de ações */}
            <div className="mb-4 rounded-md border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-100">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <h2 className="text-lg font-semibold">Workforce • Equipamentos</h2>
                        <p className="text-sm text-neutral-600 dark:text-neutral-400">
                            Cadastro e status de equipamentos (disponíveis, parados, manutenção etc.).
                        </p>
                    </div>

                    <div className="flex gap-2">
                        <input
                            value={term}
                            onChange={(e) => {
                                setTerm(e.target.value);
                                setPage(1);
                            }}
                            placeholder="Buscar por código, tipo, local..."
                            className="h-9 w-64 rounded-md border border-neutral-300 bg-white px-3 text-sm text-neutral-900 placeholder-neutral-400 outline-none
                         dark:border-neutral-700 dark:bg-neutral-950 dark:text-neutral-100 dark:placeholder-neutral-500"
                        />
                        <button
                            onClick={() => q.refetch()}
                            className="h-9 rounded-md border border-neutral-300 bg-neutral-50 px-3 text-sm text-neutral-900 hover:bg-neutral-100
                         dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100 dark:hover:bg-neutral-800"
                        >
                            Atualizar
                        </button>
                    </div>
                </div>
            </div>

            {/* Estados da consulta */}
            {q.isLoading && (
                <div className="rounded-md border border-neutral-200 bg-white p-6 text-neutral-700 dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-200">
                    Carregando equipamentos…
                </div>
            )}

            {q.isError && (
                <div className="rounded-md border border-red-300 bg-red-50 p-4 text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300">
                    {(q.error as Error)?.message || "Erro ao carregar equipamentos."}
                </div>
            )}

            {!q.isLoading && !q.isError && (
                <div className="overflow-auto rounded-md border border-neutral-200 bg-white dark:border-neutral-800 dark:bg-neutral-950">
                    <table className="min-w-[900px] w-full text-sm">
                        <thead className="bg-neutral-100 text-neutral-700 dark:bg-neutral-900 dark:text-neutral-300">
                            <tr className="text-left">
                                <Th>Empresa</Th>
                                <Th>Site</Th>
                                <Th>Armazém</Th>
                                <Th>Tipo</Th>
                                <Th>Código</Th>
                                <Th>Status</Th>
                                <Th>Local atual</Th>
                                <Th>Parking</Th>
                                <Th>Descrição</Th>
                                <Th>Serial</Th>
                                <Th>Ativo</Th>
                            </tr>
                        </thead>
                        <tbody className="text-neutral-900 dark:text-neutral-100">
                            {rows.length === 0 && (
                                <tr>
                                    <td colSpan={11} className="p-6 text-center text-neutral-500 dark:text-neutral-400">
                                        Nenhum equipamento encontrado.
                                    </td>
                                </tr>
                            )}

                            {rows.map((e) => (
                                <tr
                                    key={e.id}
                                    className="border-t border-neutral-100 hover:bg-neutral-50 dark:border-neutral-800 dark:hover:bg-neutral-900/60"
                                >
                                    <Td>{showNamed(e.company)}</Td>
                                    <Td>{showNamed(e.site)}</Td>
                                    <Td>{showNamed(e.warehouse)}</Td>
                                    <Td>
                                        <div className="flex items-center gap-2">
                                            {e.type_image ? (
                                                <img
                                                    src={e.type_image}
                                                    alt={showNamed(e.type)}
                                                    className="h-8 w-8 shrink-0 rounded object-contain bg-neutral-50 dark:bg-neutral-800"
                                                    loading="lazy"
                                                />
                                            ) : null}
                                            <span>{showNamed(e.type)}</span>
                                        </div>
                                    </Td>
                                    <Td className="font-medium">{e.code}</Td>
                                    <Td>
                                        <span className={statusBadgeClasses(e.status)}>{e.status || "—"}</span>
                                    </Td>
                                    <Td>{showNamed(e.current_location)}</Td>
                                    <Td>{showParking(e.parking_spot)}</Td>
                                    <Td className="max-w-[280px] truncate" title={e.description ?? ""}>
                                        {e.description || "—"}
                                    </Td>
                                    <Td className="whitespace-nowrap">{e.serial_no || "—"}</Td>
                                    <Td>{e.is_active ? "✓" : "—"}</Td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {!q.isLoading && !q.isError && (
                <div className="mt-3 flex items-center justify-between gap-3">
                    <div className="text-xs text-neutral-500 dark:text-neutral-400">
                        {q.data?.total ?? rows.length} itens {q.data?.endpoint ? `• fonte: ${q.data.endpoint}` : ""}
                    </div>
                    <Pagination
                        page={page}
                        pageSize={PAGE_SIZE}
                        total={q.data?.total ?? 0}
                        onPageChange={setPage}
                    />
                </div>
            )}
        </div>
    );
}

// -------------------------- Subcomponentes de tabela -------------------------
function Th({
    children,
    className = "",
    ...rest
}: React.ThHTMLAttributes<HTMLTableCellElement>) {
    return (
        <th className={cls("px-3 py-2 text-xs font-semibold uppercase tracking-wide", className)} {...rest}>
            {children}
        </th>
    );
}

function Td({
    children,
    className = "",
    ...rest
}: React.TdHTMLAttributes<HTMLTableCellElement>) {
    return (
        <td className={cls("px-3 py-2 align-top", className)} {...rest}>
            {children}
        </td>
    );
}
